import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Stack,
  Paper,
  Avatar,
} from '@mui/material';
import {
  GitHub,
  Star,
  Layers,
  Dashboard,
  Code,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

const Projects = () => {
  const { darkMode } = useThemeMode();

  const projects = [
    {
      title: 'E-Commerce Store',
      description: 'Full-stack e-commerce application with comprehensive product management, secure user authentication, shopping cart functionality, and payment processing. Features include product catalog, user profiles, order management, and admin dashboard for inventory control.',
      shortDesc: 'Complete MERN stack e-commerce platform',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      technologies: ['React', 'Express.js', 'MongoDB', 'Node.js', 'Docker', 'JWT'],
      category: 'Full Stack',
      status: 'Completed',
      icon: <Dashboard />,
      features: ['Shopping Cart', 'Payment Gateway', 'Admin Panel'],
    },
    {
      title: 'Todo List Application',
      description: 'Modern task management application with CRUD operations, user authentication, task categorization, and priority levels. Built with responsive design and real-time updates for seamless task organization and productivity tracking.',
      shortDesc: 'Feature-rich task management system',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      technologies: ['React', 'Express.js', 'MongoDB', 'Node.js', 'Docker', 'Material-UI'],
      category: 'Web App',
      status: 'Completed',
      icon: <Layers />,
      features: ['CRUD Operations', 'Task Categories', 'Priority Levels'],
    },
    {
      title: 'OfficeLogin',
      description: 'Professional office management system with employee authentication, attendance tracking, role-based access control, and administrative features. Includes employee profiles, department management, and comprehensive reporting dashboard.',
      shortDesc: 'Office management and authentication system',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      technologies: ['React', 'Express.js', 'MongoDB', 'Node.js', 'Docker', 'JWT'],
      category: 'Enterprise',
      status: 'Completed',
      icon: <Star />,
      features: ['Employee Auth', 'Attendance Tracking', 'Role Management'],
    },
  ];

  return (
    <Box id="projects" sx={{ 
      py: 2, 
      backgroundColor: darkMode 
        ? 'rgba(255, 152, 0, 0.02)' 
        : 'rgba(255, 152, 0, 0.01)' 
    }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Code sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(45deg, #1976d2, #dc004e)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Featured Projects
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              fontSize: '1.1rem',
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Here are my key projects built with MERN stack, showcasing full-stack development skills and modern web technologies
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ justifyContent: 'center', maxWidth: '1200px', mx: 'auto' }}>
          {projects.map((project, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
              <Paper
                component="a"
                href="https://github.com/iamyogeshmore?tab=repositories"
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                  height: { xs: 320, sm: 350 },
                  background: darkMode
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  border: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: darkMode
                      ? '0 20px 40px rgba(0, 0, 0, 0.4)'
                      : '0 20px 40px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none',
                    color: 'inherit',
                  },
                  '&:hover .project-overlay': {
                    opacity: 1,
                  },
                  '&:hover .project-header': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
                {/* Project Header with Enhanced Gradient */}
                <Box
                  className="project-header"
                  sx={{
                    height: { xs: 100, sm: 120 },
                    background: project.gradient,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    overflow: 'hidden',
                    transition: 'transform 0.4s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: darkMode
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(255, 255, 255, 0.1)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 100,
                      height: 100,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      zIndex: 1,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 2 }}>
                    <Avatar
                      sx={{
                        width: { xs: 40, sm: 50 },
                        height: { xs: 40, sm: 50 },
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        mr: 2,
                      }}
                    >
                      {project.icon}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'white',
                          fontWeight: 800,
                          fontSize: { xs: '1rem', sm: '1.1rem' },
                          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        }}
                      >
                        {project.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                        }}
                      >
                        {project.category}
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    label={project.status}
                    size="small"
                    sx={{
                      backgroundColor: project.status === 'Completed'
                        ? 'rgba(76, 175, 80, 0.9)'
                        : 'rgba(255, 152, 0, 0.9)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 24,
                      zIndex: 2,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  />
                </Box>

                {/* Project Content */}
                <Box sx={{ p: { xs: 2, sm: 2.5 }, height: { xs: 220, sm: 230 }, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      fontSize: '0.85rem',
                      mb: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {project.shortDesc}
                  </Typography>

                  {/* Key Features */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        mb: 0.8,
                        fontSize: '0.75rem',
                      }}
                    >
                      Key Features:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {project.features.slice(0, 3).map((feature, featureIndex) => (
                        <Chip
                          key={featureIndex}
                          label={feature}
                          size="small"
                          sx={{
                            backgroundColor: darkMode
                              ? 'rgba(100, 181, 246, 0.1)'
                              : 'rgba(25, 118, 210, 0.1)',
                            color: 'primary.main',
                            border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 18,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Technologies */}
                  <Box sx={{ mb: 2, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        mb: 0.8,
                        fontSize: '0.75rem',
                      }}
                    >
                      Technologies:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {project.technologies.slice(0, 4).map((tech, techIndex) => (
                        <Chip
                          key={techIndex}
                          label={tech}
                          size="small"
                          sx={{
                            backgroundColor: darkMode
                              ? 'rgba(220, 0, 78, 0.1)'
                              : 'rgba(220, 0, 78, 0.1)',
                            color: '#dc004e',
                            border: `1px solid ${darkMode ? 'rgba(220, 0, 78, 0.3)' : 'rgba(220, 0, 78, 0.3)'}`,
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 18,
                          }}
                        />
                      ))}
                      {project.technologies.length > 4 && (
                        <Chip
                          label={`+${project.technologies.length - 4}`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                            color: 'text.secondary',
                            fontSize: '0.6rem',
                            height: 18,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* GitHub Link */}
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1.5,
                        px: 3,
                        borderRadius: 3,
                        background: darkMode
                          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                          : 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                        border: darkMode
                          ? '1px solid rgba(255, 255, 255, 0.2)'
                          : '1px solid rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <GitHub sx={{ fontSize: 20, color: 'text.primary' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: 'text.primary',
                          fontFamily: '"JetBrains Mono", monospace',
                        }}
                      >
                        View on GitHub
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Hover Overlay with Full Description */}
                <Box
                  className="project-overlay"
                  sx={{
                    position: 'absolute',
                    top: { xs: 100, sm: 120 },
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: darkMode
                      ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                    backdropFilter: 'blur(20px)',
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      fontWeight: 800,
                      color: 'text.primary',
                      fontSize: '0.95rem',
                      textAlign: 'center',
                    }}
                  >
                    Click to View Repository
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      fontSize: '0.8rem',
                      textAlign: 'center',
                      fontWeight: 500,
                      mb: 2,
                    }}
                  >
                    {project.description}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                      px: 2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #1976d2 0%, #dc004e 100%)',
                      color: 'white',
                    }}
                  >
                    <GitHub sx={{ fontSize: 18 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    >
                      github.com/iamyogeshmore
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Projects;